// Insurer-facing claim tracking for full/Section 2 incidents. FormsByAir
// API access is still pending (requested from Rothbury) -- "Submit to
// Insurer" builds and shows the payload this claim would carry, mapped
// from data already in insurance_incidents/drivers/vehicles, and marks the
// claim ready_to_submit. Once API access lands, that button starts making
// a real POST instead of just building the preview -- this screen's shape
// doesn't need to change.
import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, RefreshCw, Upload, FileText, Trash2, Pencil, Check, Download, ExternalLink, Lock, Undo2, Mail } from "lucide-react";
import {
  InsuranceClaim,
  InsuranceIncident,
  InsuranceClaimDocument,
  ClaimType,
  ClaimStatus,
  fetchClaims,
  fetchIncidents,
  fetchIncidentPhotos,
  fetchIncidentDamageMarkers,
  fetchIncidentThirdParties,
  createClaim,
  updateClaim,
  fetchClaimDocuments,
  addClaimDocument,
  updateClaimDocument,
  deleteClaimDocument,
} from "@/lib/incidentReports";
import { generateClaimPdf, getClaimPdfBase64 } from "@/lib/generateClaimPdf";
import { downloadRepairAuthorizationPdf, getRepairAuthorizationPdfBase64 } from "@/lib/generateRepairAuthorizationPdf";
import { ClaimData, ClaimSection, buildClaimSections, loadClaimData } from "@/lib/claimSections";
import { parseEmailList } from "@/lib/insuranceBrokerSettings";
import { DAMAGE_DIAGRAM_VIEWS, resolveOverlayAssetKey, overlayPublicUrl, renderDiagramWithMarkers } from "@/lib/damageDiagram";
import { formatDate } from "@/lib/dates";

const claimDocumentUrl = (path: string) => supabase.storage.from("claim-documents").getPublicUrl(path).data.publicUrl;

const STATUS_BADGE: Record<ClaimStatus, string> = {
  not_submitted: "bg-gray-700 text-gray-200",
  ready_to_submit: "bg-yellow-800 text-yellow-200",
  submitted: "bg-blue-800 text-blue-200",
  in_progress: "bg-blue-900 text-blue-300",
  settled: "bg-green-800 text-green-200",
  declined: "bg-red-900 text-red-300",
};

// The insurer's "any X? if so, details" declaration questions -- nullable
// text on drivers/vehicles (see migration 0049). Rather than making Janelle
// fill these in as standing profile edits, they're asked here, at the
// moment a claim actually needs them, and only for whichever ones are
// still blank. Answering here also patches the driver/vehicle record so
// the next claim for the same driver/vehicle doesn't ask again.
type DeclarationKey =
  | "dl_conditions"
  | "convictions_last_7_years"
  | "accidents_claims_last_5_years"
  | "modification_details"
  | "finance_details"
  | "other_insurance_details";
const DRIVER_DECLARATION_KEYS: DeclarationKey[] = ["dl_conditions", "convictions_last_7_years", "accidents_claims_last_5_years"];
const VEHICLE_DECLARATION_KEYS: DeclarationKey[] = ["modification_details", "finance_details", "other_insurance_details"];
const DECLARATION_LABEL: Record<DeclarationKey, string> = {
  dl_conditions: "Driver -- Licence Conditions",
  convictions_last_7_years: "Driver -- Convictions in Last 7 Years",
  accidents_claims_last_5_years: "Driver -- Accidents/Claims in Last 5 Years",
  modification_details: "Vehicle -- Modifications",
  finance_details: "Vehicle -- Finance",
  other_insurance_details: "Vehicle -- Other Insurance",
};

const CLAIM_TYPE_LABEL: Record<ClaimType, string> = {
  full: "Full Claim",
  section2: "Section 2",
  third_party_at_fault: "Third Party At Fault",
};

interface InsuranceClaimsTabProps {
  /** Jumps to the Incidents tab and opens the given incident -- used for
   * anything not directly editable here (fault classification, repairer
   * details, third parties, photos, and the damage diagram). */
  onEditIncident?: (incidentId: string) => void;
}

const InsuranceClaimsTab: React.FC<InsuranceClaimsTabProps> = ({ onEditIncident }) => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) only sees claims tied to their linked driver(s) OR vehicle(s)
  // (via the claim's parent incident -- either match is enough), read-
  // only unless explicitly given edit rights. Unrestricted (the default
  // -- everyone today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const canEdit = canEditTab(effectivePermissions, "insurance");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;
  const scopedVehicleNumbers = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedVehicleNumbers;
  const isIncidentInScope = (i: { driver_name?: string | null; vehicle_number?: string | null }): boolean => {
    if (!scopedDriverNames && !scopedVehicleNumbers) return true;
    if (i.driver_name && scopedDriverNames?.includes(i.driver_name)) return true;
    if (i.vehicle_number && scopedVehicleNumbers?.includes(i.vehicle_number)) return true;
    return false;
  };

  const [claims, setClaims] = useState<InsuranceClaim[]>([]);
  const [incidents, setIncidents] = useState<InsuranceIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [newIncidentId, setNewIncidentId] = useState("");
  const [newClaimType, setNewClaimType] = useState<ClaimType>("full");
  const [blankDeclarationFields, setBlankDeclarationFields] = useState<DeclarationKey[]>([]);
  const [declarationAnswers, setDeclarationAnswers] = useState<Partial<Record<DeclarationKey, string>>>({});

  const [selected, setSelected] = useState<InsuranceClaim | null>(null);
  const [claimSections, setClaimSections] = useState<ClaimSection[] | null>(null);
  const [diagramImages, setDiagramImages] = useState<{ owner: string; view: string; label: string; dataUrl: string }[]>([]);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [closing, setClosing] = useState(false);

  // Manual status + reference tracking -- FormsByAir API access is still
  // pending, so submitting actually happens outside DMS; this just records
  // that it happened. Broker Reference and Claim Number are two distinct,
  // separately-issued numbers: the broker (e.g. Rothbury) gives one when
  // the claim is first sent to them, the insurer gives a different one once
  // it's actually approved.
  const [statusValue, setStatusValue] = useState<ClaimStatus>("not_submitted");
  const [brokerReferenceValue, setBrokerReferenceValue] = useState("");
  const [claimNumberValue, setClaimNumberValue] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);

  // Full driver/vehicle/broker data behind the claim -- loaded once in
  // openDetail and kept around so the email-send dialogs (broker/adviser/
  // repair agent) can build their PDF attachments without refetching.
  const [claimData, setClaimData] = useState<ClaimData | null>(null);

  // Email-send dialog -- shared by the "Email Broker" / "Email Claims
  // Adviser" / "Email Repair Agent" buttons, distinguished by `emailRole`.
  const [emailRole, setEmailRole] = useState<"broker" | "adviser" | "repairer" | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  // Which uploaded Claim Documents to attach -- unticked by default (a
  // manual incident report or internal note shouldn't go to the broker just
  // because it's on the claim), picked per send via the checkboxes below.
  const [emailDocIds, setEmailDocIds] = useState<Set<string>>(new Set());

  // Update Declarations -- the same 6 fields the New Claim dialog asks
  // about, but always editable (pre-filled with current values) here so
  // Janelle can fix or fill them in if new info comes in, right up until
  // the claim's been submitted or closed.
  const [editDeclarations, setEditDeclarations] = useState<Partial<Record<DeclarationKey, string>>>({});
  const [savingDeclarations, setSavingDeclarations] = useState(false);

  const [documents, setDocuments] = useState<InsuranceClaimDocument[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const load = async () => {
    setLoading(true);
    const [c, i] = await Promise.all([fetchClaims(), fetchIncidents()]);
    const scopedIncidents = i.filter(isIncidentInScope);
    const incidentIds = new Set(scopedIncidents.map((inc) => inc.id));
    setClaims(c.filter((claim) => incidentIds.has(claim.incident_id)));
    setIncidents(scopedIncidents);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopedDriverNames, scopedVehicleNumbers]);

  const incidentById = (id: string) => incidents.find((i) => i.id === id);

  // not_at_fault is claimable too -- our driver isn't liable, but we still
  // need our own insurer to handle it (repairs, recovering the cost from
  // the at-fault party's insurer) rather than there being nothing to claim.
  const claimableIncidents = incidents.filter(
    (i) =>
      (i.fault_classification === "at_fault" ||
        i.fault_classification === "section2" ||
        i.fault_classification === "section2_with_repair_cost" ||
        i.fault_classification === "not_at_fault") &&
      !claims.some((c) => c.incident_id === i.id)
  );

  const selectIncidentForNewClaim = async (incidentId: string) => {
    setNewIncidentId(incidentId);
    // Default the claim type to match how the incident was classified --
    // not_at_fault incidents are almost always a third-party-at-fault
    // claim, not a full/section2 one.
    const incident = claimableIncidents.find((i) => i.id === incidentId);
    if (incident?.fault_classification === "not_at_fault") setNewClaimType("third_party_at_fault");
    else if (newClaimType === "third_party_at_fault") setNewClaimType("full");

    setDeclarationAnswers({});
    setBlankDeclarationFields([]);
    if (!incident) return;
    const [{ data: driver }, { data: vehicle }] = await Promise.all([
      supabase
        .from("drivers")
        .select("dl_conditions, convictions_last_7_years, accidents_claims_last_5_years")
        .eq("driver_name", incident.driver_name)
        .maybeSingle(),
      incident.vehicle_number
        ? supabase
            .from("vehicles")
            .select("modification_details, finance_details, other_insurance_details")
            .eq("vehicle_number", incident.vehicle_number)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    const blanks: DeclarationKey[] = [];
    DRIVER_DECLARATION_KEYS.forEach((k) => {
      if (!driver || !(driver as Record<string, string | null>)[k]) blanks.push(k);
    });
    if (incident.vehicle_number) {
      VEHICLE_DECLARATION_KEYS.forEach((k) => {
        if (!vehicle || !(vehicle as Record<string, string | null>)[k]) blanks.push(k);
      });
    }
    setBlankDeclarationFields(blanks);
  };

  const resetNewClaimForm = () => {
    setShowAdd(false);
    setNewIncidentId("");
    setBlankDeclarationFields([]);
    setDeclarationAnswers({});
  };

  const addClaim = async () => {
    if (!canEdit) return;
    if (!newIncidentId) {
      toast({ title: "Error", description: "Select an incident", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const claim = await createClaim({ incident_id: newIncidentId, claim_type: newClaimType });

      // Answering the "not in DMS yet" questions above patches the
      // driver/vehicle record too, so the next claim for the same
      // driver/vehicle doesn't ask again.
      const incident = claimableIncidents.find((i) => i.id === newIncidentId);
      if (incident) {
        const driverPatch: Record<string, string> = {};
        DRIVER_DECLARATION_KEYS.forEach((k) => {
          const v = declarationAnswers[k]?.trim();
          if (v) driverPatch[k] = v;
        });
        if (Object.keys(driverPatch).length > 0) {
          await supabase.from("drivers").update(driverPatch).eq("driver_name", incident.driver_name);
        }
        if (incident.vehicle_number) {
          const vehiclePatch: Record<string, string> = {};
          VEHICLE_DECLARATION_KEYS.forEach((k) => {
            const v = declarationAnswers[k]?.trim();
            if (v) vehiclePatch[k] = v;
          });
          if (Object.keys(vehiclePatch).length > 0) {
            await supabase.from("vehicles").update(vehiclePatch).eq("vehicle_number", incident.vehicle_number);
          }
        }
      }

      // Bundle the incident's own scene/damage/licence photos in as claim
      // documents too, so everything needed to submit sits in one place
      // alongside anything the insurer sends back -- not just referenced
      // from the incident record.
      const photos = await fetchIncidentPhotos(newIncidentId);
      for (let i = 0; i < photos.length; i++) {
        const photo = photos[i];
        const { data: fileBlob, error: downloadError } = await supabase.storage.from("incident-reports").download(photo.storage_path);
        if (downloadError || !fileBlob) continue;
        const fileName = photo.storage_path.split("/").pop() || `photo_${i + 1}.jpg`;
        const destPath = `claims/${claim.id}/incident_photo_${i + 1}_${fileName}`;
        const { error: uploadError } = await supabase.storage.from("claim-documents").upload(destPath, fileBlob, {
          contentType: fileBlob.type || "image/jpeg",
          upsert: true,
        });
        if (uploadError) continue;
        await addClaimDocument({
          claim_id: claim.id,
          label: `Incident photo ${i + 1}`,
          storage_path: destPath,
          file_name: fileName,
          notes: null,
          uploaded_by: "admin",
        });
      }
      toast({ title: "Claim created", description: "Tracking as not yet submitted." });
      resetNewClaimForm();
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSaving(false);
  };

  const openDetail = async (claim: InsuranceClaim) => {
    setSelected(claim);
    setDocuments([]);
    setEditingDocId(null);
    setClaimSections(null);
    setDiagramImages([]);
    setEditDeclarations({});
    setStatusValue(claim.status);
    setBrokerReferenceValue(claim.broker_reference || "");
    setClaimNumberValue(claim.claim_number || "");
    setClaimData(null);
    fetchClaimDocuments(claim.id).then(setDocuments);
    const incident = incidentById(claim.incident_id);
    if (!incident) return;
    const data = await loadClaimData(incident, claim);
    setClaimData(data);
    setClaimSections(buildClaimSections(data));
    setEditDeclarations({
      dl_conditions: data.driver?.dl_conditions || "",
      convictions_last_7_years: data.driver?.convictions_last_7_years || "",
      accidents_claims_last_5_years: data.driver?.accidents_claims_last_5_years || "",
      modification_details: data.vehicle?.modification_details || "",
      finance_details: data.vehicle?.finance_details || "",
      other_insurance_details: data.vehicle?.other_insurance_details || "",
    });

    // Same composed diagrams the PDF embeds -- rendered here too so what's
    // reviewed on screen before Submit to Insurer matches the download.
    // Covers our own vehicle plus any third party an admin has picked a
    // body-type silhouette for.
    const [overlayAssetKey, markers, thirdParties] = await Promise.all([
      resolveOverlayAssetKey(data.vehicle?.vehicle_type ?? null),
      fetchIncidentDamageMarkers(incident.id),
      fetchIncidentThirdParties(incident.id),
    ]);
    const images: { owner: string; view: string; label: string; dataUrl: string }[] = [];
    if (overlayAssetKey) {
      for (const { view, label } of DAMAGE_DIAGRAM_VIEWS) {
        const viewMarkers = markers.filter((m) => m.view === view && !m.third_party_id);
        if (viewMarkers.length === 0) continue;
        const rendered = await renderDiagramWithMarkers(overlayPublicUrl(overlayAssetKey, view), viewMarkers);
        if (rendered) images.push({ owner: "Our Vehicle", view, label, dataUrl: rendered.dataUrl });
      }
    }
    for (const tp of thirdParties) {
      const tpAssetKey = await resolveOverlayAssetKey(tp.overlay_vehicle_type);
      if (!tpAssetKey) continue;
      for (const { view, label } of DAMAGE_DIAGRAM_VIEWS) {
        const viewMarkers = markers.filter((m) => m.view === view && m.third_party_id === tp.id);
        if (viewMarkers.length === 0) continue;
        const rendered = await renderDiagramWithMarkers(overlayPublicUrl(tpAssetKey, view), viewMarkers);
        if (rendered) images.push({ owner: tp.other_driver_name || "Other Party", view, label, dataUrl: rendered.dataUrl });
      }
    }
    setDiagramImages(images);
  };

  // A claim can be edited (declarations updated) right up until it's been
  // submitted to the insurer or manually closed -- after that, corrections
  // should go through the insurer directly rather than silently changing
  // what was actually submitted.
  const claimIsEditable = (claim: InsuranceClaim) =>
    !claim.is_closed && (claim.status === "not_submitted" || claim.status === "ready_to_submit");

  const saveDeclarations = async () => {
    if (!selected || !canEdit) return;
    const incident = incidentById(selected.incident_id);
    if (!incident) return;
    setSavingDeclarations(true);
    try {
      const driverPatch: Record<string, string | null> = {};
      DRIVER_DECLARATION_KEYS.forEach((k) => {
        driverPatch[k] = editDeclarations[k]?.trim() || null;
      });
      await supabase.from("drivers").update(driverPatch).eq("driver_name", incident.driver_name);

      if (incident.vehicle_number) {
        const vehiclePatch: Record<string, string | null> = {};
        VEHICLE_DECLARATION_KEYS.forEach((k) => {
          vehiclePatch[k] = editDeclarations[k]?.trim() || null;
        });
        await supabase.from("vehicles").update(vehiclePatch).eq("vehicle_number", incident.vehicle_number);
      }

      toast({ title: "Saved", description: "Declarations updated." });
      const data = await loadClaimData(incident, selected);
      setClaimSections(buildClaimSections(data));
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingDeclarations(false);
  };

  const closeClaim = async (reason: string) => {
    if (!selected || !canEdit) return;
    setClosing(true);
    try {
      const patch: Partial<InsuranceClaim> = { is_closed: true, closed_at: new Date().toISOString(), closed_reason: reason };
      // Closing as "Settled / Accepted" means the insurer has settled it, so the Status badge
      // moves to settled with it (previously the two were separate and drifted apart). "Repaired"
      // is the car being fixed, which says nothing about the insurer, so Status is left alone.
      const settling = reason === "Settled / Accepted" && selected.status !== "settled";
      if (settling) {
        patch.status = "settled";
        if (selected.status === "not_submitted" || selected.status === "ready_to_submit") {
          patch.submitted_at = selected.submitted_at ?? new Date().toISOString();
          patch.submitted_by = selected.submitted_by ?? "admin";
        }
      }
      await updateClaim(selected.id, patch);
      setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
      if (settling) setStatusValue("settled");
      toast({ title: "Claim closed", description: settling ? `${reason} -- status set to settled` : reason });
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setClosing(false);
  };

  const reopenClaim = async () => {
    if (!selected || !canEdit) return;
    setClosing(true);
    try {
      const patch = { is_closed: false, closed_at: null, closed_reason: null };
      await updateClaim(selected.id, patch);
      setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
      toast({ title: "Claim reopened" });
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setClosing(false);
  };

  // One click on a status button saves it straight away (and only the status -- anything typed
  // into the reference boxes but not yet saved is left alone until Save is pressed).
  const setStatusNow = async (next: ClaimStatus) => {
    if (!selected || !canEdit || next === selected.status) return;
    setSavingStatus(true);
    try {
      const wasSubmittedYet = selected.status !== "not_submitted" && selected.status !== "ready_to_submit";
      const isNowSubmittedYet = next !== "not_submitted" && next !== "ready_to_submit";
      const patch: Partial<InsuranceClaim> = { status: next };
      if (isNowSubmittedYet && !wasSubmittedYet) {
        patch.submitted_at = new Date().toISOString();
        patch.submitted_by = "admin";
      }
      await updateClaim(selected.id, patch);
      setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
      setStatusValue(next);
      toast({ title: "Status updated", description: next.replace(/_/g, " ") });
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingStatus(false);
  };

  const saveClaimStatus = async () => {
    if (!selected || !canEdit) return;
    setSavingStatus(true);
    try {
      const wasSubmittedYet = selected.status !== "not_submitted" && selected.status !== "ready_to_submit";
      const isNowSubmittedYet = statusValue !== "not_submitted" && statusValue !== "ready_to_submit";
      const patch: Partial<InsuranceClaim> = {
        status: statusValue,
        broker_reference: brokerReferenceValue.trim() || null,
        claim_number: claimNumberValue.trim() || null,
      };
      // Stamp submitted_at/submitted_by the first time it crosses into an
      // actually-submitted state -- don't overwrite it on later edits (e.g.
      // fixing a typo in the reference number, or moving submitted -> settled).
      if (isNowSubmittedYet && !wasSubmittedYet) {
        patch.submitted_at = new Date().toISOString();
        patch.submitted_by = "admin";
      }
      await updateClaim(selected.id, patch);
      setSelected((prev) => (prev ? { ...prev, ...patch } : prev));
      toast({ title: "Claim updated" });
      load();
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setSavingStatus(false);
  };

  const openEmailDialog = (role: "broker" | "adviser" | "repairer") => {
    if (!selected || !claimData) return;
    const { incident, vehicle, broker } = claimData;
    setEmailRole(role);
    setEmailDocIds(new Set());
    if (role === "broker") {
      setEmailTo(broker.brokerEmail);
      setEmailSubject(`New Claim -- ${incident.driver_name || "Driver"} (${formatDate(incident.incident_date)})`);
      setEmailMessage(
        `Hi,\n\nPlease find attached the claim details for ${incident.driver_name || "the driver"} (${formatDate(incident.incident_date)}).\n\nThanks,\nTaxis Gold & Black`
      );
    } else if (role === "adviser") {
      setEmailTo(broker.claimsAdviserEmail);
      setEmailSubject(
        `${selected.broker_reference ? `Claim ${selected.broker_reference}` : "New Claim"} -- ${incident.driver_name || "Driver"} (${formatDate(
          incident.incident_date
        )})`
      );
      setEmailMessage(
        `Hi,\n\nPlease find attached the claim details for ${incident.driver_name || "the driver"} (${formatDate(incident.incident_date)}).${
          selected.broker_reference ? ` Broker Reference: ${selected.broker_reference}.` : ""
        }\n\nThanks,\nTaxis Gold & Black`
      );
    } else {
      setEmailTo(incident.repairer_email || "");
      setEmailSubject(`Repair Authorisation -- ${vehicle?.vehicle_number || incident.vehicle_number || "Vehicle"}`);
      setEmailMessage(
        `Hi,\n\nPlease find attached the repair authorisation for ${vehicle?.registration || vehicle?.vehicle_number || "the vehicle"}.${
          selected.claim_number ? ` Claim Number: ${selected.claim_number}.` : ""
        }\n\nThanks,\nTaxis Gold & Black`
      );
    }
  };

  const closeEmailDialog = () => {
    setEmailRole(null);
    setEmailTo("");
    setEmailSubject("");
    setEmailMessage("");
  };

  const sendClaimEmail = async () => {
    if (!selected || !claimData || !emailRole) return;
    const to = parseEmailList(emailTo);
    if (to.length === 0) {
      toast({ title: "Error", description: "Enter at least one email address.", variant: "destructive" });
      return;
    }
    setSendingEmail(true);
    try {
      const { base64, fileName } =
        emailRole === "repairer" ? getRepairAuthorizationPdfBase64(claimData) : await getClaimPdfBase64(claimData.incident, selected);
      const html = emailMessage
        .split("\n")
        .map((line) => (line ? `<p>${line}</p>` : "<br/>"))
        .join("");
      const extraAttachments = documents
        .filter((d) => emailDocIds.has(d.id))
        .map((d) => ({ filename: d.label || d.file_name || "attachment", url: claimDocumentUrl(d.storage_path) }));
      const res = await supabase.functions.invoke("send-insurance-email", {
        body: { to, subject: emailSubject, html, pdfBase64: base64, fileName, extraAttachments },
      });
      if (res.error) throw res.error;
      toast({ title: "Email sent", description: `Sent to ${to.join(", ")}.` });
      closeEmailDialog();
    } catch (e) {
      // Graceful fallback: still download the PDF so it can be sent manually.
      if (emailRole === "repairer") downloadRepairAuthorizationPdf(claimData);
      else await generateClaimPdf(claimData.incident, selected);
      toast({
        title: "Could not auto-email",
        description: `Downloaded the PDF instead -- send it manually. (${e instanceof Error ? e.message : String(e)})`,
        variant: "destructive",
      });
    }
    setSendingEmail(false);
  };

  const downloadPdf = async () => {
    if (!selected) return;
    const incident = incidentById(selected.incident_id);
    if (!incident) {
      toast({ title: "Error", description: "Couldn't find the underlying incident.", variant: "destructive" });
      return;
    }
    setDownloadingPdf(true);
    try {
      await generateClaimPdf(incident, selected);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setDownloadingPdf(false);
  };

  const uploadDocuments = async (files: FileList | null) => {
    if (!files || files.length === 0 || !selected || !canEdit) return;
    setUploadingDoc(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const path = `claims/${selected.id}/${Date.now()}_${i}_${file.name}`;
        const { error: uploadError } = await supabase.storage.from("claim-documents").upload(path, file, {
          contentType: file.type || "application/octet-stream",
          upsert: true,
        });
        if (uploadError) throw uploadError;
        const doc = await addClaimDocument({
          claim_id: selected.id,
          label: file.name,
          storage_path: path,
          file_name: file.name,
          notes: null,
          uploaded_by: "admin",
        });
        setDocuments((prev) => [doc, ...prev]);
      }
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
    setUploadingDoc(false);
  };

  const startEditDoc = (doc: InsuranceClaimDocument) => {
    setEditingDocId(doc.id);
    setEditLabel(doc.label || "");
    setEditNotes(doc.notes || "");
  };

  const saveEditDoc = async (id: string) => {
    if (!canEdit) return;
    try {
      await updateClaimDocument(id, { label: editLabel || null, notes: editNotes || null });
      setDocuments((prev) => prev.map((d) => (d.id === id ? { ...d, label: editLabel || null, notes: editNotes || null } : d)));
      setEditingDocId(null);
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  const removeDoc = async (doc: InsuranceClaimDocument) => {
    if (!canEdit) return;
    try {
      await deleteClaimDocument(doc.id);
      await supabase.storage.from("claim-documents").remove([doc.storage_path]);
      setDocuments((prev) => prev.filter((d) => d.id !== doc.id));
    } catch (e) {
      toast({ title: "Error", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    }
  };

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-xl text-primary">Claims</CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} className="golden-button" disabled={claimableIncidents.length === 0}>
            <Plus className="w-4 h-4 mr-2" />
            New Claim
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          FormsByAir API access is still pending from Rothbury -- submit each claim to the insurer outside DMS, then
          open it here to record its status and claim reference number.
        </p>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : claims.length === 0 ? (
          <p className="text-sm text-muted-foreground">No claims yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Open/Closed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {claims.map((claim) => {
                  const incident = incidentById(claim.incident_id);
                  return (
                    <TableRow key={claim.id} className="cursor-pointer hover:bg-yellow-900/10" onClick={() => openDetail(claim)}>
                      <TableCell className="font-medium">{incident?.driver_name || "--"}</TableCell>
                      <TableCell>{incident?.vehicle_number || "--"}</TableCell>
                      <TableCell>{incident?.incident_date ? formatDate(incident.incident_date) : "--"}</TableCell>
                      <TableCell>{CLAIM_TYPE_LABEL[claim.claim_type]}</TableCell>
                      <TableCell>
                        <Badge className={STATUS_BADGE[claim.status]}>{claim.status.replace(/_/g, " ")}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={claim.is_closed ? "bg-green-900 text-green-300" : "bg-yellow-900 text-yellow-300"}>
                          {claim.is_closed ? "Closed" : "Open"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={showAdd} onOpenChange={(open) => (open ? setShowAdd(true) : resetNewClaimForm())}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Claim</DialogTitle>
            <DialogDescription>
              Only incidents already classified At Fault, Section 2, or Not At Fault can become a claim.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Incident</Label>
              <Select value={newIncidentId} onValueChange={selectIncidentForNewClaim}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an incident" />
                </SelectTrigger>
                <SelectContent>
                  {claimableIncidents.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.driver_name} -- {formatDate(i.incident_date)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Claim Type</Label>
              <Select value={newClaimType} onValueChange={(v) => setNewClaimType(v as ClaimType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(CLAIM_TYPE_LABEL) as ClaimType[]).map((t) => (
                    <SelectItem key={t} value={t}>
                      {CLAIM_TYPE_LABEL[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {blankDeclarationFields.length > 0 && (
              <div className="border-t border-border pt-3 space-y-2">
                <Label className="text-sm font-semibold">Not in DMS yet</Label>
                <p className="text-xs text-muted-foreground">
                  Answering here saves it to the driver/vehicle record too, so it won't ask again next time. Leave blank if it
                  doesn't apply.
                </p>
                {blankDeclarationFields.map((k) => (
                  <div key={k}>
                    <Label className="text-xs">{DECLARATION_LABEL[k]}</Label>
                    <Input
                      value={declarationAnswers[k] || ""}
                      onChange={(e) => setDeclarationAnswers((d) => ({ ...d, [k]: e.target.value }))}
                      placeholder="Leave blank if none"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={resetNewClaimForm}>
              Cancel
            </Button>
            <Button onClick={addClaim} disabled={saving} className="golden-button">
              Create Claim
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>Claim Payload Preview</DialogTitle>
                <DialogDescription className="flex items-center gap-2 flex-wrap">
                  <span>
                    Status: <Badge className={STATUS_BADGE[selected.status]}>{selected.status.replace(/_/g, " ")}</Badge>
                  </span>
                  <Badge className={selected.is_closed ? "bg-green-900 text-green-300" : "bg-yellow-900 text-yellow-300"}>
                    {selected.is_closed ? `Closed${selected.closed_reason ? ` -- ${selected.closed_reason}` : ""}` : "Open"}
                  </Badge>
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    onEditIncident?.(selected.incident_id);
                    setSelected(null);
                  }}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  Edit Incident / Diagram
                </Button>
                {selected.is_closed ? (
                  <Button variant="outline" size="sm" onClick={reopenClaim} disabled={closing}>
                    <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                    {closing ? "Reopening..." : "Reopen Claim"}
                  </Button>
                ) : (
                  <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" onClick={() => closeClaim("Repaired")} disabled={closing}>
                      Mark Repaired (Close)
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => closeClaim("Settled / Accepted")} disabled={closing}>
                      Mark Settled (Close)
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                <div className="rounded-lg border border-border bg-background/40 p-3 space-y-2">
                  <Label className="text-sm font-semibold">Status &amp; Claim Reference</Label>
                  <div>
                    <Label className="text-xs">Status (click to change)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {(Object.keys(STATUS_BADGE) as ClaimStatus[]).map((s) => {
                        const active = selected.status === s;
                        return (
                          <Button
                            key={s}
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={!canEdit || savingStatus}
                            onClick={() => setStatusNow(s)}
                            className={active ? `${STATUS_BADGE[s]} border-transparent ring-2 ring-offset-1 ring-offset-background ring-primary` : ""}
                            aria-pressed={active}
                          >
                            {s.replace(/_/g, " ")}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap items-end">
                    <div className="flex-1 min-w-[160px]">
                      <Label className="text-xs">Broker Reference</Label>
                      <Input
                        value={brokerReferenceValue}
                        onChange={(e) => setBrokerReferenceValue(e.target.value)}
                        placeholder="Given when sent to broker"
                      />
                    </div>
                    <div className="flex-1 min-w-[160px]">
                      <Label className="text-xs">Claim Number</Label>
                      <Input
                        value={claimNumberValue}
                        onChange={(e) => setClaimNumberValue(e.target.value)}
                        placeholder="Given once approved"
                      />
                    </div>
                    <Button size="sm" onClick={saveClaimStatus} disabled={savingStatus} className="golden-button">
                      {savingStatus ? "Saving..." : "Save references"}
                    </Button>
                  </div>
                  {selected.submitted_at && (
                    <p className="text-xs text-muted-foreground">
                      Submitted {formatDate(selected.submitted_at)}
                      {selected.submitted_by ? ` by ${selected.submitted_by}` : ""}
                    </p>
                  )}
                </div>

                <div className="rounded-lg border border-border bg-background/40 p-3 space-y-2">
                  <Label className="text-sm font-semibold">Send</Label>
                  <p className="text-xs text-muted-foreground">
                    Emails the claim details (or, for the repair agent, a short repair authorisation) as a PDF
                    attachment.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => openEmailDialog("broker")} disabled={!claimData}>
                      <Mail className="w-3.5 h-3.5 mr-1.5" />
                      Email Broker
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => openEmailDialog("adviser")} disabled={!claimData}>
                      <Mail className="w-3.5 h-3.5 mr-1.5" />
                      Email Claims Adviser
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEmailDialog("repairer")}
                      disabled={!claimData || !claimData.incident.repairer_email}
                      title={!claimData?.incident.repairer_email ? "Add a repairer email on the incident first" : undefined}
                    >
                      <Mail className="w-3.5 h-3.5 mr-1.5" />
                      Email Repair Agent
                    </Button>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Everything below is what goes on the claim -- check it over before submitting to the insurer.
                </p>
                {claimSections === null ? (
                  <p className="text-sm text-muted-foreground">Loading...</p>
                ) : (
                  <div className="space-y-3">
                    {claimSections.map((section) => (
                      <div key={section.title} className="rounded-lg border border-border bg-background/40 p-3">
                        <p className="text-xs font-semibold text-yellow-500 uppercase mb-1.5">{section.title}</p>
                        <div className="space-y-0.5">
                          {section.rows.map(([label, value]) => (
                            <div key={label} className="flex justify-between gap-3 text-xs">
                              <span className="text-muted-foreground shrink-0">{label}</span>
                              <span className="text-foreground text-right">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {diagramImages.length > 0 && (
                  <div className="border-t border-border pt-3 space-y-2">
                    <p className="text-xs font-semibold text-yellow-500 uppercase">Damage Diagram</p>
                    <div className="flex flex-wrap gap-3">
                      {diagramImages.map((d) => (
                        <div key={`${d.owner}-${d.view}`} className="text-center">
                          <img src={d.dataUrl} alt={`${d.owner} -- ${d.label}`} className="w-32 h-auto rounded border border-border" />
                          <p className="text-xs text-muted-foreground mt-1">{d.owner} -- {d.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-border pt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-semibold">Update Declarations</Label>
                    {!claimIsEditable(selected) && <Lock className="w-3.5 h-3.5 text-muted-foreground" />}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {claimIsEditable(selected)
                      ? "If new info comes in, update it here -- saves straight to the driver/vehicle record."
                      : "This claim has been submitted or closed, so these are locked. Reopen the claim to edit them again."}
                  </p>
                  {[...DRIVER_DECLARATION_KEYS, ...VEHICLE_DECLARATION_KEYS].map((k) => (
                    <div key={k}>
                      <Label className="text-xs">{DECLARATION_LABEL[k]}</Label>
                      <Input
                        value={editDeclarations[k] || ""}
                        onChange={(e) => setEditDeclarations((d) => ({ ...d, [k]: e.target.value }))}
                        placeholder="Leave blank if none"
                        disabled={!claimIsEditable(selected)}
                      />
                    </div>
                  ))}
                  {claimIsEditable(selected) && (
                    <Button size="sm" variant="outline" onClick={saveDeclarations} disabled={savingDeclarations}>
                      {savingDeclarations ? "Saving..." : "Save Declarations"}
                    </Button>
                  )}
                </div>

                <div className="border-t border-border pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold">Claim Documents</Label>
                    <label
                      className={`inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:border-yellow-500/60 ${
                        uploadingDoc ? "opacity-60 pointer-events-none" : "cursor-pointer"
                      }`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      {uploadingDoc ? "Uploading..." : "Upload"}
                      <input type="file" multiple className="hidden" disabled={uploadingDoc} onChange={(e) => uploadDocuments(e.target.files)} />
                    </label>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Acceptance letters, settlement confirmations, or anything else the insurer sends back on this claim.
                  </p>
                  {documents.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No documents uploaded yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.id} className="rounded-lg border border-border p-2 text-xs">
                          {editingDocId === doc.id ? (
                            <div className="space-y-1.5">
                              <Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} placeholder="Label" className="h-7 text-xs" />
                              <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Notes (optional)" className="h-7 text-xs" />
                              <div className="flex gap-1.5">
                                <Button size="sm" className="h-6 px-2 golden-button" onClick={() => saveEditDoc(doc.id)}>
                                  <Check className="w-3 h-3 mr-1" />
                                  Save
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setEditingDocId(null)}>
                                  Cancel
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-2">
                              <a
                                href={claimDocumentUrl(doc.storage_path)}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-start gap-1.5 text-foreground hover:text-yellow-400 min-w-0"
                              >
                                <FileText className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                <span className="min-w-0">
                                  <span className="block truncate font-medium">{doc.label || doc.file_name}</span>
                                  {doc.notes && <span className="block text-muted-foreground">{doc.notes}</span>}
                                  <span className="block text-muted-foreground">{formatDate(doc.created_at)}</span>
                                </span>
                              </a>
                              <div className="flex gap-1 shrink-0">
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => startEditDoc(doc)}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeDoc(doc)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setSelected(null)}>
                  Close
                </Button>
                <Button variant="outline" onClick={downloadPdf} disabled={downloadingPdf}>
                  <Download className="w-4 h-4 mr-2" />
                  {downloadingPdf ? "Building PDF..." : "Download PDF"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!emailRole} onOpenChange={(open) => !open && closeEmailDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {emailRole === "broker" && "Email Broker"}
              {emailRole === "adviser" && "Email Claims Adviser"}
              {emailRole === "repairer" && "Email Repair Agent"}
            </DialogTitle>
            <DialogDescription>
              {emailRole === "repairer"
                ? "Sends the repair authorisation (vehicle, company, and claim reference details) as a PDF attachment -- not in the email body."
                : "Sends the full claim details as a PDF attachment -- not in the email body."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">To (comma-separate multiple addresses)</Label>
              <Input value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="name@example.com" />
            </div>
            <div>
              <Label className="text-xs">Subject</Label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <textarea
                className="w-full min-h-[100px] rounded-md border border-border bg-background/40 px-3 py-2 text-sm"
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
              />
            </div>
            {documents.length > 0 && (
              <div>
                <Label className="text-xs">Also attach</Label>
                <div className="mt-1.5 space-y-1.5 max-h-[160px] overflow-y-auto rounded-md border border-border p-2">
                  {documents.map((doc) => (
                    <label key={doc.id} className="flex items-start gap-2 text-xs cursor-pointer">
                      <Checkbox
                        className="mt-0.5"
                        checked={emailDocIds.has(doc.id)}
                        onCheckedChange={(checked) =>
                          setEmailDocIds((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(doc.id);
                            else next.delete(doc.id);
                            return next;
                          })
                        }
                      />
                      <span className="min-w-0">
                        <span className="block truncate">{doc.label || doc.file_name}</span>
                        {doc.notes && <span className="block text-muted-foreground truncate">{doc.notes}</span>}
                      </span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  Untick anything internal -- nothing here is attached unless you tick it.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeEmailDialog}>
              Cancel
            </Button>
            <Button onClick={sendClaimEmail} disabled={sendingEmail} className="golden-button">
              <Mail className="w-4 h-4 mr-2" />
              {sendingEmail ? "Sending..." : "Send"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InsuranceClaimsTab;
